/** Detach — the mark trails behind your cursor, detached along the line of travel. */
import type { CreateModule } from '../../core/module-api';
import { cfgNum } from '../../core/module-api';
import type { Point } from '../../core/scene';
import { defaultStyle } from '../../core/scene';
import { buildSmoothPath, distance } from '../../core/geometry';

const ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 6l5 5-5 5"/><path d="M19 11H8a4 4 0 0 0-4 4v3"/></svg>';

export function createDetachShapes(): CreateModule {
  let pts: Point[] = [];
  let angles: number[] = [];
  let drawing = false;

  return {
    manifest: {
      id: 'detach',
      kind: 'create',
      name: 'Detach',
      apiVersion: 1,
      description: 'The mark trails behind your cursor, detached along the direction of travel.',
      icon: ICON,
      params: [{ kind: 'slider', key: 'distance', label: 'Distance', min: 0, max: 300, step: 1, default: 100 }],
    },

    onPointerDown(s) {
      pts = [{ x: s.x, y: s.y }];
      angles = [0];
      drawing = true;
    },

    onPointerMove(s) {
      if (!drawing) return;
      const p = { x: s.x, y: s.y };
      const last = pts[pts.length - 1];
      if (!last || distance(last, p) >= 2) {
        angles.push(last ? Math.atan2(p.y - last.y, p.x - last.x) : 0);
        pts.push(p);
      }
    },

    onPointerUp() {
      drawing = false;
    },

    getLiveShapes(ctx) {
      if (pts.length === 0) return [];
      const dist = cfgNum(ctx.config, 'distance', 100);
      const off = pts.map((p, i) => {
        // The first point has no travel direction yet; trail it along the second's.
        const a = i === 0 ? (angles[1] ?? 0) : angles[i];
        return { x: p.x - Math.cos(a) * dist, y: p.y - Math.sin(a) * dist };
      });
      const style = defaultStyle();
      style.color = { ...ctx.ink };
      style.lineWidth = 2;
      return [ctx.newShape({ path: buildSmoothPath(off), style })];
    },

    onClear() {
      pts = [];
      angles = [];
    },

    onCommit() {
      pts = [];
      angles = [];
    },
  };
}
